import { useState, useEffect, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category", label: "漫画分类" },
  { to: "/category/completed", label: "完结大全" },
  { to: "/about", label: "关于我们" },
];

export default function MangaNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const isHome = pathname === "/";
  const onHero = isHome && !scrolled;

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/category?search=${encodeURIComponent(kw)}` : "/category");
    setKeyword("");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap text-sm font-medium transition-colors ${
      onHero
        ? isActive
          ? "text-background-50"
          : "text-background-50/80 hover:text-background-50"
        : isActive
          ? "text-primary-600"
          : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        onHero ? "bg-transparent" : "border-b border-background-200/70 bg-background-50/90 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" title="土豪漫画网首页">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg ${
              onHero ? "bg-background-50/20 text-background-50" : "bg-primary-500 text-background-50"
            }`}
          >
            <i className="ri-fire-fill" aria-hidden="true" />
          </span>
          <span className={`font-heading text-xl font-bold ${onHero ? "text-background-50" : "text-foreground-950"}`}>
            土豪漫画
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          role="search"
          aria-label="搜索漫画"
          className={`hidden items-center gap-2 rounded-full border px-3.5 py-2 transition-colors md:flex ${
            onHero
              ? "border-background-50/30 bg-background-50/15"
              : "border-background-200 bg-background-100/80 focus-within:border-primary-300"
          }`}
        >
          <i
            className={`ri-search-line text-sm ${onHero ? "text-background-50/80" : "text-foreground-400"}`}
            aria-hidden="true"
          />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索漫画名称"
            className={`w-40 bg-transparent text-sm outline-none lg:w-48 ${
              onHero ? "placeholder:text-background-50/60 text-background-50" : "placeholder:text-foreground-400 text-foreground-900"
            }`}
            aria-label="搜索漫画名称"
          />
          <button
            type="submit"
            className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-medium transition-colors ${
              onHero ? "bg-background-50 text-foreground-900 hover:bg-background-100" : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className={`flex h-10 w-10 items-center justify-center rounded-md text-xl md:hidden ${
            onHero ? "text-background-50" : "text-foreground-800"
          }`}
          aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={menuOpen}
        >
          <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true" />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary-100 text-primary-700" : "text-foreground-800 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <form onSubmit={handleSearch} role="search" className="mt-3 flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3.5 py-2">
            <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画名称"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="搜索漫画名称"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-2.5 py-0.5 text-sm font-medium text-background-50 hover:bg-primary-600">
              搜索
            </button>
          </form>
        </div>
      )}
    </header>
  );
}